import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Checkbox
} from '@mui/material';
import { MoreVert, ArrowDownward, ArrowUpward, Remove, DragIndicator, PictureAsPdf, Settings, CalendarMonth } from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import projectService, { ScheduleItem, ScheduleItemDisplay, Project } from '../../services/projectService';
import SimpleGantt from './SimpleGantt';
import { Notification } from '../common';

interface ScheduleCardProps {
  projectId: string;
  projectName: string;
}

type ViewMode = 'table' | 'gantt';

const mapApiToDisplay = (item: ScheduleItem): ScheduleItemDisplay => {
  return {
    id_task: item.id_task || String(item.id),
    id: item.id,
    ssp: item.ssp,
    task: item.name,
    days: item.days,
    startDate: item.start_date,
    endDate: item.end_date,
    predecessor: item.predecessor || '',
    value: item.value || 0,
    resource: item.resource || '',
    notes: item.notes || '',
    expected_progress: item.expected_progress,
    expected_progress_date: item.expected_progress_date,
    actual_progress: item.actual_progress,
    actual_progress_date: item.actual_progress_date
  };
};

const ScheduleCard: React.FC<ScheduleCardProps> = ({ projectId, projectName }) => {
  const toLocalISO = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const [items, setItems] = React.useState<ScheduleItemDisplay[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [view, setView] = React.useState<ViewMode>('table');
  const [zoom, setZoom] = React.useState<'year' | 'month' | 'day'>('month');
  const [showControl, setShowControl] = React.useState<boolean>(true);

  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [form, setForm] = React.useState({
    ssp: '',
    task: '',
    days: 1,
    start_date: toLocalISO(new Date()),
    // end_date zawsze wynikowe = start_date + days - 1
    end_date: toLocalISO(new Date()),
    value: 0,
    notes: '',
    predecessor_task_id: '' as string | number,
    predecessor_type: 'FS' as 'FS' | 'FF' | 'SS' | 'SF',
    predecessor_lag: 0,
    section: 1 as number,
    level: 1 as 1 | 2 | 3
  });
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = React.useState<number | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [statusRowId, setStatusRowId] = React.useState<number | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = React.useState<boolean>(false);
  const [statusForm, setStatusForm] = React.useState<{ actual_progress: number; date: string }>({ actual_progress: 0, date: new Date().toISOString().slice(0,10) });
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({
    sel: 16,
    drag: 14,
    id: 30,
    ssp: 44,
    opis: 220,
    days: 80,
    start: 130,
    end: 130,
    predecessor: 140,
    value: 140,
    notes: 160,
    progress: 90,
    progressDate: 140,
    control: 60,
    actions: 80
  });
  const [dragCol, setDragCol] = React.useState<{ id: string; startX: number; startW: number } | null>(null);
  const ganttRef = React.useRef<HTMLDivElement>(null);
  const ganttHiddenRef = React.useRef<HTMLDivElement>(null);
  const startInputRef = React.useRef<HTMLInputElement | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [pdfCols, setPdfCols] = React.useState<{ id: boolean; ssp: boolean; name: boolean; days: boolean; start?: boolean; end?: boolean; predecessor?: boolean; value?: boolean; notes?: boolean; progress?: boolean; progressDate?: boolean }>({ id: true, ssp: true, name: true, days: true, start: false, end: false, predecessor: false, value: false, notes: false, progress: false, progressDate: false });
  const [pdfMode, setPdfMode] = React.useState<'baseline' | 'control'>('control');
  const [pdfScale, setPdfScale] = React.useState<'year' | 'month' | 'day'>(zoom);
  const [printMeta, setPrintMeta] = React.useState<{ scheduleDate: string; address: string; client: string; contractSubject?: string; generalContractor: string }>({ scheduleDate: new Date().toISOString().slice(0,10), address: '', client: '', contractSubject: '', generalContractor: 'Unitresco sp. z o. o., ul. Żurawia 71, 15-540 Białystok' });
  const fetchedMetaRef = React.useRef(false);
  const VIEW_STORAGE_KEY = React.useMemo(() => `ganttView:${projectId}`, [projectId]);
  const META_STORAGE_KEY = React.useMemo(() => `ganttPrintMeta:${projectId}`, [projectId]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const csvEscape = (val: any) => {
    const s = String(val ?? '');
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes(';')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const downloadCsvOfCurrentItems = async () => {
    const header = ['SPP','Opis','Dni','Rozpoczęcie','Zakończenie','Poprzednik','Wartość','Uwagi','Zaaw. [%]','Data stanu'];
    const lines = [header.join(',')];
    items.forEach((it) => {
      const row = [
        csvEscape(it.ssp || ''),
        csvEscape(it.task || ''),
        csvEscape(it.days ?? ''),
        csvEscape(it.startDate || ''),
        csvEscape(it.endDate || ''),
        csvEscape(it.predecessor || ''),
        csvEscape(it.value ?? 0),
        csvEscape(it.notes || ''),
        csvEscape(it.actual_progress ?? 0),
        csvEscape(it.actual_progress_date || '')
      ];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0,10);
    a.download = `harmonogram-${projectName}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  

  // Powiadomienie lokalne
  const [notify, setNotify] = React.useState<{ open: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', type: 'success' });

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(16, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.id]: newW }));
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true });
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  // Wczytaj zapisane ustawienia "Widok" i metadane ustawień drukowania
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(VIEW_STORAGE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj?.cols) setPdfCols(obj.cols);
        if (obj?.scale) { setPdfScale(obj.scale); setZoom(obj.scale); }
        if (obj?.mode) { setPdfMode(obj.mode); setShowControl(obj.mode === 'control'); }
      }
    } catch {}
    try {
      const rawMeta = localStorage.getItem(META_STORAGE_KEY);
      if (rawMeta) {
        const meta = JSON.parse(rawMeta);
        setPrintMeta(prev => ({ ...prev, ...meta }));
      }
    } catch {}
  }, [VIEW_STORAGE_KEY, META_STORAGE_KEY]);

  const renderTh = (keyId: string, label: string, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ fontWeight: 700, width: `${columnWidths[keyId]}px`, position: 'relative', whiteSpace: 'nowrap', pr: 0.25, pl: 0.25 }}>
      {label}
      <Box title="Przeciągnij, aby zmienić szerokość" onMouseDown={(e) => setDragCol({ id: keyId, startX: e.clientX, startW: columnWidths[keyId] })}
           sx={{ position: 'absolute', right: 0, top: 0, width: '6px', cursor: 'col-resize', height: '100%', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }} />
    </TableCell>
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const apiItems = await projectService.getScheduleItemsByProjectId(projectId);
      const mapped = apiItems.map(mapApiToDisplay);
      setItems(mapped);
    } catch (err: any) {
      console.error('Nie udało się pobrać harmonogramu z API:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Jednorazowe zaciągnięcie danych projektu do ustawień PDF
  React.useEffect(() => {
    const fetchMetaOnce = async () => {
      if (fetchedMetaRef.current) return;
      try {
        const proj = await projectService.getProjectById(projectId);
        if (proj) {
          setPrintMeta(prev => ({
            scheduleDate: prev.scheduleDate || new Date().toISOString().slice(0,10),
            address: prev.address || proj.investment_address || '',
            client: prev.client || proj.client_name || '',
            contractSubject: prev.contractSubject || (proj as any).contract_subject || '',
            generalContractor: prev.generalContractor || 'Unitresco sp. z o. o., ul. Żurawia 71, 15-540 Białystok'
          }));
          fetchedMetaRef.current = true;
        }
      } catch (e) {
        // pomiń błąd – ustawienia można wypełnić ręcznie
      }
    };
    fetchMetaOnce();
  }, [projectId, projectName]);

  // Automat numeracji działów: jeśli nie ma żadnego działu, pierwszy = 1
  const computeNextSection = (list: ScheduleItemDisplay[]): number => {
    const defined = list.map(i => i.section).filter(Boolean) as number[];
    if (defined.length === 0) return 1;
    return Math.max(...defined) || 1;
  };

  // Wyznacz kolejną pozycję w dziale w bieżącej kolejności
  const computeNextPositionInSection = (list: ScheduleItemDisplay[], section: number): number => {
    return list.filter(i => i.section === section).length + 1;
  };

  // Przeliczanie SSP według kolejności listy: SSP = "dział.pozycja_w_dziale"
  // Nowy algorytm SSP: jeśli brak wcięć → numeracja 1,2,3; jeśli są wcięcia → hierarchiczna np. 2.1, 2.2.3
  const recomputeSsp = (list: ScheduleItemDisplay[]): ScheduleItemDisplay[] => {
    // wyznacz wcięcia – jeśli brak, nadaj 0
    const indents = list.map(i => (typeof i.indent === 'number' ? i.indent! : (i.section ? 1 : 0)));
    // jeśli wszystkie 0 → płaska numeracja 1..n
    if (indents.every(x => x === 0)) {
      return list.map((it, idx) => ({ ...it, ssp: String(idx + 1), indent: 0 }));
    }
    // hierarchiczna – licznik dla każdego poziomu
    const counters: number[] = [];
    return list.map((item, idx) => {
      const level = Math.max(0, indents[idx] || 0);
      // skróć lub rozciągnij tablicę liczników do level+1
      counters.length = level + 1;
      if (!Number.isFinite(counters[level])) counters[level] = 0;
      counters[level] += 1;
      // wyzeruj głębsze poziomy
      for (let i = level + 1; i < counters.length; i++) counters[i] = 0;
      const parts = counters.slice(0, level + 1);
      const ssp = parts.join('.');
      return { ...item, ssp, indent: level };
    });
  };

  // Zapisz zmiany SSP do backendu (tylko gdy się zmieniły)
  const persistReorder = async (before: ScheduleItemDisplay[], after: ScheduleItemDisplay[]) => {
    // Wykryj zmiany po ID, nie po indeksie – indeksy zmieniają się przy DnD
    const changed = after.filter(a => {
      const prev = before.find(b => b.id === a.id);
      return prev && prev.ssp !== a.ssp;
    });
    if (changed.length === 0) return;
    try {
      // Dwuetapowo, aby uniknąć konfliktu unikalności (project_id, ssp)
      const tmpTag = `TMP-${Date.now()}`;
      for (let i = 0; i < changed.length; i++) {
        const c = changed[i];
        await projectService.updateScheduleItem(c.id, { project_id: projectId, ssp: `${tmpTag}-${i + 1}` } as any);
      }
      for (const c of changed) {
        await projectService.updateScheduleItem(c.id, { project_id: projectId, ssp: c.ssp } as any);
      }
    } catch (e) {
      console.warn('Nie udało się zapisać nowej kolejności – pozostawiam lokalnie.', e);
    }
  };

  // SSP helpers
  const parseSsp = (ssp?: string): { section: number | null; index: number | null } => {
    if (!ssp) return { section: null, index: null };
    const m = String(ssp).match(/^(\d+)\.(\d+)$/);
    if (!m) return { section: null, index: null };
    return { section: Number(m[1]), index: Number(m[2]) };
  };

  const getBlockStarts = (list: ScheduleItemDisplay[]): number[] => {
    const starts: number[] = [];
    list.forEach((item, idx) => {
      const { index } = parseSsp(item.ssp);
      if (index === 1) starts.push(idx);
    });
    // jeśli brak jednoznacznych startów, całość traktujemy jako jeden blok od 0
    if (starts.length === 0) return [0];
    return starts;
  };

  const indentRight = () => {
    if (!selectedIds.length) return;
    setItems(prev => {
      const before = [...prev];
      const updated = before.map(item => selectedIds.includes(item.id)
        ? { ...item, indent: Math.max(0, (item.indent ?? 0) + 1) }
        : item);
      const after = recomputeSsp(updated);
      persistReorder(before, after);
      return after;
    });
  };

  const indentLeft = () => {
    if (!selectedIds.length) return;
    setItems(prev => {
      const before = [...prev];
      const updated = before.map(item => selectedIds.includes(item.id)
        ? { ...item, indent: Math.max(0, (item.indent ?? 0) - 1) }
        : item);
      const after = recomputeSsp(updated);
      persistReorder(before, after);
      return after;
    });
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    const section = computeNextSection(items);
    const position = computeNextPositionInSection(items, section);
    const todayIso = toLocalISO(new Date());
    setForm({ ssp: `${section}.${position}`, task: '', days: 1, start_date: todayIso, end_date: todayIso, value: 0, notes: '', predecessor_task_id: '', predecessor_type: 'FS', predecessor_lag: 0, section, level: 1 });
    setIsDialogOpen(true);
  };
  const handleOpenEdit = (row: ScheduleItemDisplay) => {
    setEditingId(row.id);
    setForm({
      ssp: row.ssp,
      task: row.task,
      days: row.days,
      start_date: row.startDate,
      end_date: row.endDate || row.startDate,
      value: row.value || 0,
      notes: row.notes || '',
      predecessor_task_id: '',
      predecessor_type: 'FS',
      predecessor_lag: 0,
      section: row.section ?? 1,
      level: (row.level as 1|2|3) ?? 1
    });
    // Parsowanie poprzednika np. "2FS+10dn"
    if (row.predecessor) {
      const m = String(row.predecessor).trim().match(/^(\d+)\s*(FS|FF|SS|SF)([+-]\d+)?/i);
      if (m) {
        setForm(prev => ({
          ...prev,
          predecessor_task_id: Number(m[1]),
          predecessor_type: m[2].toUpperCase() as any,
          predecessor_lag: m[3] ? Number(m[3]) : 0
        }));
      }
    }
    setIsDialogOpen(true);
  };
  const handleClose = () => setIsDialogOpen(false);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRowId(id);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRowId(null);
  };

  const openStatusMenu = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    e.stopPropagation();
    setStatusMenuAnchor(e.currentTarget);
    setStatusRowId(id);
  };
  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusRowId(null);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next: any = { ...prev };
      if (name === 'days' || name === 'value') {
        next[name] = Number(value) || 0;
      } else if (name === 'start_date') {
        next.start_date = value;
        // Zawsze przelicz zakończenie jako start + days - 1 (inkluzywnie)
        const start = new Date(value + 'T00:00:00');
        const end = new Date(start.getTime() + Math.max(0, (Number(prev.days) || 0) - 1) * 86400000);
        next.end_date = toLocalISO(end);
      } else if (name === 'end_date') {
        // Pole zakończenia jest wynikowe – brak ręcznej edycji
        next.end_date = prev.end_date;
      } else {
        next[name] = value;
      }
      if (name === 'days') {
        const start = new Date(prev.start_date + 'T00:00:00');
        const end = new Date(start.getTime() + Math.max(0, (Number(value) || 0) - 1) * 86400000);
        next.end_date = toLocalISO(end);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    // Wyliczenia dat na podstawie poprzednika/relacji/przesunięcia i czasu
    const toIso = (d: Date) => {
      // lokalne YYYY-MM-DD bez strefy
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    const addDaysAbs = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
    const computeStart = (): Date => {
      const pid = Number(form.predecessor_task_id);
      if (!pid) return new Date(form.start_date + 'T00:00:00');
      const pred = items.find(it => it.id === pid);
      if (!pred) return new Date(form.start_date + 'T00:00:00');
      const predStart = new Date(pred.startDate + 'T00:00:00');
      const predEnd = new Date(pred.endDate + 'T00:00:00'); // endExclusive
      if (form.predecessor_type === 'FS') {
        // start = dzień po zakończeniu poprzednika + lag
        return addDaysAbs(predEnd, 1 + (Number(form.predecessor_lag) || 0));
      } else if (form.predecessor_type === 'SS') {
        // starty tego samego dnia + lag
        return addDaysAbs(predStart, Number(form.predecessor_lag) || 0);
      } else if (form.predecessor_type === 'FF') {
        // koniec tego samego dnia + lag → start = endExclusive - days
        const endExclusive = addDaysAbs(predEnd, Number(form.predecessor_lag) || 0);
        return addDaysAbs(endExclusive, -Number(form.days) + 1);
      } else { // SF
        // koniec zależny od startu poprzednika + lag → start = endExclusive - days
        const endExclusive = addDaysAbs(predStart, Number(form.predecessor_lag) || 0);
        return addDaysAbs(endExclusive, -Number(form.days) + 1);
      }
    };
    const computedStart = computeStart();
    // Jeżeli brak poprzednika i użytkownik edytował zakres, przelicz dni z dat
    let effectiveDays = Number(form.days) || 1;
    if (!form.predecessor_task_id && form.end_date) {
      const end = new Date(form.end_date + 'T00:00:00');
      effectiveDays = Math.max(1, Math.round((end.getTime() - computedStart.getTime()) / 86400000) + 1);
    }

    const predecessor = form.predecessor_task_id
      ? (() => {
          const base = `${form.predecessor_task_id}${form.predecessor_type}`;
          const lag = Number(form.predecessor_lag) || 0;
          return lag === 0 ? base : `${base}${lag > 0 ? `+${lag}` : `${lag}`}dn`;
        })()
      : '';
    const payload = {
      project_id: projectId,
      ssp: editingId == null ? String(items.length + 1) : form.ssp,
      // Backend może oczekiwać kolumny 'task' lub 'name' – wyślij obie
      name: form.task,
      task: form.task,
      days: effectiveDays,
      start_date: toIso(computedStart),
      value: Number(form.value) || 0,
      notes: form.notes,
      predecessor,
      section: form.section,
      level: form.level
    };
    try {
      if (editingId == null) {
        await projectService.createScheduleItem(payload as any);
      } else {
        const { project_id, ssp, ...updateData } = payload as any; // nie aktualizuj ssp podczas edycji pól formularza
        await projectService.updateScheduleItem(editingId, updateData);
      }
      const successMessage = editingId == null ? 'Dodano pozycję w harmonogramie' : 'Zapisano zmiany pozycji';
      handleClose();
      // po zapisie ściągnij świeże dane (bez cache)
      await load();
      const todayIso2 = toLocalISO(new Date());
      setForm({ ssp: String(items.length + 1), task: '', days: 1, start_date: todayIso2, end_date: todayIso2, value: 0, notes: '', predecessor_task_id: '', predecessor_type: 'FS', predecessor_lag: 0, section: computeNextSection(items), level: 1 });
      setTimeout(() => setNotify({ open: true, type: 'success', message: successMessage }), 250);
    } catch (err) {
      console.error('Błąd zapisu elementu harmonogramu:', err);
      setNotify({ open: true, type: 'error', message: 'Nie udało się zapisać pozycji w harmonogramie' });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await projectService.deleteScheduleItem(id);
      await load();
    } catch (err) {
      console.warn('API deleteScheduleItem niedostępne – usuwam lokalny wiersz (placeholder).');
      setItems((prev) => prev.filter((p) => p.id !== id));
    }
  };

  // Obliczenia do widoku "Kontrola stanu"
  const todayIso = new Date().toISOString().slice(0, 10);
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
  const computeExpected = (row: ScheduleItemDisplay): number => {
    const start = new Date(row.startDate + 'T00:00:00');
    const endExclusive = new Date(row.endDate + 'T00:00:00');
    const today = new Date(todayIso + 'T12:00:00');
    const total = Math.max(1, Math.round((endExclusive.getTime() - start.getTime()) / 86400000));
    if (today <= start) return 0;
    if (today >= endExclusive) return 100;
    const elapsed = Math.round((today.getTime() - start.getTime()) / 86400000);
    return clamp((elapsed / total) * 100, 0, 100);
  };

  const controlIcon = (row: ScheduleItemDisplay) => {
    const actual = row.actual_progress ?? 0;
    const expected = computeExpected(row);
    const delta = actual - expected;
    if (delta < -5) return <Tooltip title={`Opóźnienie ${delta.toFixed(0)}%`}><ArrowDownward sx={{ color: 'error.main' }} fontSize="small" /></Tooltip>;
    if (delta > 5) return <Tooltip title={`Przyspieszenie +${delta.toFixed(0)}%`}><ArrowUpward sx={{ color: 'success.main' }} fontSize="small" /></Tooltip>;
    return <Tooltip title="Zgodnie z planem"><Remove sx={{ color: 'warning.main' }} fontSize="small" /></Tooltip>;
  };

  // Usuwamy nawigację klawiszami – przesuwanie tylko drag & drop za uchwyt po lewej

  // Pomocnicze: załaduj font TTF (z polskimi znakami) do jsPDF w runtime
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
    return window.btoa(binary);
  };
  const ensurePolishFont = async (pdf: jsPDF) => {
    if ((window as any).__notoSansRegistered) { (pdf as any).setFont('NotoSans', 'normal'); return; }
    const tryLoad = async (url: string) => {
      const res = await fetch(url, { mode: 'cors' as RequestMode });
      if (!res.ok) return false;
      const buf = await res.arrayBuffer();
      const base64 = arrayBufferToBase64(buf);
      (pdf as any).addFileToVFS('NotoSans.ttf', base64);
      (pdf as any).addFont('NotoSans.ttf', 'NotoSans', 'normal');
      (pdf as any).setFont('NotoSans', 'normal');
      (window as any).__notoSansRegistered = true;
      return true;
    };
    // 1) Spróbuj z CDN (Noto Sans latin subset, zawiera PL znaki)
    const cdnUrl = 'https://cdn.jsdelivr.net/npm/@fontsource/noto-sans/files/noto-sans-latin-400-normal.ttf';
    try {
      if (await tryLoad(cdnUrl)) return;
    } catch {}
    // 2) Lokalny fallback (jeśli dodasz TTF do public/fonts)
    try { await tryLoad(`${process.env.PUBLIC_URL || ''}/fonts/DejaVuSans.ttf`); } catch {}
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, value) => value && setView(value)}
        >
          <ToggleButton value="table">Harmonogram</ToggleButton>
          <ToggleButton value="gantt">Wykres Gantta</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1} alignItems="center">
          {view === 'gantt' && (
            <ToggleButtonGroup size="small" exclusive value={showControl ? 'control' : 'baseline'} onChange={(_, val) => { if (val) setShowControl(val === 'control'); }}>
              <ToggleButton value="baseline">Harmonogram bazowy</ToggleButton>
              <ToggleButton value="control">Kontrola stanu</ToggleButton>
            </ToggleButtonGroup>
          )}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="zoom-label">Skala czasu</InputLabel>
            <Select labelId="zoom-label" label="Skala czasu" value={zoom} onChange={(e) => setZoom(e.target.value as any)}>
              <MenuItem value="year">Rok</MenuItem>
              <MenuItem value="month">Miesiące</MenuItem>
              <MenuItem value="day">Dni</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleOpenAdd}>
            Dodaj zadanie
          </Button>
          {/* Usunięto stare przyciski CSV */}
        </Stack>
      </Box>

      {/* Przyciski wcięcia/wycięcia + PDF */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={indentLeft} disabled={!selectedIds.length}>{'<-'}</Button>
          <Button size="small" variant="outlined" onClick={indentRight} disabled={!selectedIds.length}>{'->'}</Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" color="secondary" onClick={async () => {
            // Jeśli są pozycje → pobierz CSV z bieżącymi danymi, w przeciwnym razie pobierz szablon
            try {
              if (items && items.length > 0) {
                await downloadCsvOfCurrentItems();
              } else {
                const blob = await projectService.downloadScheduleCsvTemplate(projectId);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `harmonogram-szablon-${projectName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }
            } catch (e) {
              setNotify({ open: true, type: 'error', message: 'Nie udało się pobrać pliku CSV' });
            }
          }}>Pobierz</Button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => {
            const inputEl = e.currentTarget;
            const file = inputEl.files?.[0];
            if (!file) return;
            (async () => {
              try {
                await projectService.importScheduleFromCsv(projectId, file);
                setNotify({ open: true, type: 'success', message: 'Zaimportowano harmonogram z CSV' });
                load();
              } catch (err) {
                console.error(err);
                setNotify({ open: true, type: 'error', message: 'Import CSV nie powiódł się' });
              } finally {
                try { if (inputEl) inputEl.value = ''; } catch {}
              }
            })();
          }} />
          <Button size="small" variant="outlined" onClick={() => fileInputRef.current?.click()}>Importuj</Button>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={() => setPdfDialogOpen(true)}>Widok</Button>
          <Button size="small" variant="outlined" startIcon={<Settings />} onClick={() => setSettingsOpen(true)}>Ustawienia</Button>
        </Stack>
      </Box>

      {view === 'table' ? (
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee' }}>
          <Table size="small" sx={{
            '& th': { py: 0.4, fontSize: '0.78rem', whiteSpace: 'nowrap' },
            '& td': { py: 0.2, fontSize: '0.78rem' }
          }}>
            <TableHead>
              <TableRow>
                {renderTh('id', 'ID', 'left')}
                {renderTh('drag', '', 'left')}
                {renderTh('sel', '', 'left')}
                {renderTh('ssp', 'SPP', 'left')}
                {renderTh('opis', 'Opis', 'left')}
                {renderTh('days', 'Dni', 'right')}
                {renderTh('start', 'Rozpoczęcie', 'left')}
                {renderTh('end', 'Zakończenie', 'left')}
                {renderTh('predecessor', 'Poprzednik', 'left')}
                {renderTh('value', 'Wartość', 'right')}
                {renderTh('notes', 'Uwagi', 'left')}
                {renderTh('progress', 'Zaaw. [%]', 'right')}
                {renderTh('progressDate', 'Data stanu', 'left')}
                {renderTh('control', '', 'center')}
                {renderTh('actions', 'Akcje', 'center')}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Wiersz nagłówkowy projektu (opis wyrównany do kolumny Opis) */}
              <TableRow key="project-header">
                <TableCell sx={{ backgroundColor: '#424242', color: '#fff', borderColor: '#424242' }} />
                <TableCell sx={{ backgroundColor: '#424242', color: '#fff', borderColor: '#424242' }} />
                <TableCell sx={{ backgroundColor: '#424242', color: '#fff', borderColor: '#424242' }}>0</TableCell>
                <TableCell sx={{ backgroundColor: '#424242', color: '#fff', borderColor: '#424242' }} />
                <TableCell colSpan={12} sx={{ fontWeight: 600, backgroundColor: '#424242', color: '#fff', borderColor: '#424242' }}>
                  {projectName}
                </TableCell>
              </TableRow>

              {items.map((row, idx) => (
                <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}
                  sx={{ backgroundColor: (() => { const lvl = row.indent ?? 0; const nextLvl = items[idx + 1]?.indent ?? 0; const isSection = nextLvl > lvl; return isSection ? `rgba(0,0,0,${Math.min(0.06 + lvl*0.06, 0.18)})` : 'transparent'; })() }}
                  draggable onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(row.id));
                }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => {
                  e.preventDefault();
                  const draggedId = Number(e.dataTransfer.getData('text/plain'));
                  if (!draggedId || draggedId === row.id) return;
                  setItems(prev => {
                    const before = [...prev];
                    const current = [...prev];
                    const fromIndex = current.findIndex(r => r.id === draggedId);
                    const toIndex = current.findIndex(r => r.id === row.id);
                    if (fromIndex < 0 || toIndex < 0) return prev;
                    const [moved] = current.splice(fromIndex, 1);
                    current.splice(toIndex, 0, moved);
                    // przelicz SSP wg działów
                    const after = recomputeSsp(current);
                    // zapisz w tle (bez blokowania UI) – backend sortuje po SPP, więc wystarczy aktualizacja SPP
                    persistReorder(before, after);
                    return after;
                  });
                }}>
                  <TableCell sx={{ width: `${columnWidths.id}px`, px: 0.25, color: 'text.secondary' }}>{row.id}</TableCell>
                  <TableCell sx={{ width: `${columnWidths.drag}px`, cursor: 'grab', px: 0.25 }}>
                    <DragIndicator sx={{ color: 'text.disabled', fontSize: 14 }} />
                  </TableCell>
                  <TableCell sx={{ width: `${columnWidths.sel}px`, px: 0.25 }}>
                    <Checkbox size="small" sx={{ transform: 'scale(0.85)', p: 0 }} checked={selectedIds.includes(row.id)} onChange={(e) => {
                      setSelectedIds(prev => e.target.checked ? (prev.includes(row.id) ? prev : [...prev, row.id]) : prev.filter(id => id !== row.id));
                    }} />
                  </TableCell>
                  <TableCell sx={{ width: `${columnWidths.spp ?? columnWidths.ssp}px`, px: 0.25 }}>{row.ssp}</TableCell>
                  <TableCell sx={{ pl: `${(row.indent ?? 0) * 16}px` }}>{row.task}</TableCell>
                  <TableCell align="right">{row.days}</TableCell>
                  <TableCell>{row.startDate}</TableCell>
                  <TableCell>{row.endDate}</TableCell>
                  <TableCell>{(() => {
                    // Bezpieczne renderowanie poprzednika
                    const raw = String(row.predecessor ?? '').trim();
                    if (!raw) return '';
                    // Jeśli jest już w formacie "IDRELACJA[+/-LAG]dn" → pokaż bez zmian
                    if (/^(\d+)\s*(FS|FF|SS|SF)([+-]\d+)?/i.test(raw)) return raw;
                    // Jeśli przychodzi w formacie SSP (np. "1.1") → spróbuj zamienić na ID
                    const found = items.find(it => it.ssp === raw);
                    return found ? `${found.id}FS+0dn` : raw;
                  })()}</TableCell>
                  <TableCell align="right">{(() => {
                    const v = Number(row.value || 0);
                    if (!isFinite(v) || v <= 0) return '0';
                    const k = Math.round(v / 1000);
                    return `${k}k`;
                  })()}</TableCell>
                  <TableCell>{row.notes}</TableCell>
                  <TableCell align="right">{(row.actual_progress ?? 0).toFixed(0)}%</TableCell>
                  <TableCell>{row.actual_progress_date ?? '—'}</TableCell>
                  <TableCell>{controlIcon(row)}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={(e) => openMenu(e, row.id)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <SimpleGantt
          items={items}
          zoom={zoom}
          showControl={showControl}
          visibleColumns={pdfCols}
          showActions={true}
          innerRef={ganttRef}
          onEdit={(row: ScheduleItemDisplay) => handleOpenEdit(row)}
          onDelete={(id: number) => handleDelete(id)}
          onControl={(row: ScheduleItemDisplay) => { setStatusRowId(row.id); setStatusForm({ actual_progress: row.actual_progress ?? 0, date: new Date().toISOString().slice(0,10) }); setStatusDialogOpen(true); }}
        />
      )}

      {/* Ukryty render do podglądu/eksportu – korzysta z bieżących ustawień w oknie Widok */}
      <Box sx={{ position: 'absolute', left: '-10000px', top: 0 }}>
        <SimpleGantt
          items={items}
          zoom={pdfScale}
          showControl={pdfMode === 'control'}
          visibleColumns={pdfCols}
          showActions={false}
          innerRef={ganttHiddenRef}
          onEdit={() => undefined}
          onDelete={() => undefined}
        />
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => {
          const row = items.find(i => i.id === menuRowId);
          if (row) handleOpenEdit(row);
          closeMenu();
        }}>Edytuj</MenuItem>
        <MenuItem onClick={() => {
          const row = items.find(i => i.id === menuRowId);
          if (row) {
            setStatusRowId(row.id);
            setStatusForm({ actual_progress: row.actual_progress ?? 0, date: new Date().toISOString().slice(0,10) });
            setStatusDialogOpen(true);
          }
          closeMenu();
        }}>Kontrola zaawansowania</MenuItem>
        <MenuItem onClick={() => {
          if (menuRowId != null) handleDelete(menuRowId);
          closeMenu();
        }} sx={{ color: 'error.main' }}>Usuń</MenuItem>
      </Menu>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingId == null ? 'Dodaj zadanie' : 'Edytuj zadanie'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* SSP – wyświetlane i przeliczane automatycznie */}
            <TextField label="SSP (automatycznie)" name="ssp" value={form.ssp} fullWidth InputProps={{ readOnly: true }} />
            {/* Zadanie */}
            <TextField label="Zadanie" name="task" value={form.task} onChange={handleChange} fullWidth />
            {/* Czas */}
            <TextField
              label="Czas [dni]"
              name="days"
              type="text"
              value={String(form.days ?? '')}
              onChange={(e) => {
                const raw = e.target.value.replace(/\s+/g, '');
                if (raw === '') { setForm(prev => ({ ...prev, days: '' as any })); return; }
                if (/^\d+$/.test(raw)) { setForm(prev => ({ ...prev, days: Math.max(0, Number(raw)) as any })); }
              }}
              onBlur={() => setForm(prev => ({ ...prev, days: Math.max(0, Number(prev.days) || 0) }))}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            {/* Suwak dla czasu (0-50 dni) */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Czas (dni) – suwak: {Number(form.days) || 0}</Typography>
              <Slider
                value={Number.isFinite(Number(form.days)) ? Number(form.days) : 0}
                onChange={(_, v) => setForm(prev => {
                  const newDays = Array.isArray(v) ? Number(v[0]) : Number(v);
                  const start = new Date(prev.start_date + 'T00:00:00');
                  const end = new Date(start.getTime() + Math.max(0, newDays - 1) * 86400000);
                  return { ...prev, days: newDays, end_date: toLocalISO(end) } as any;
                })}
                min={0}
                max={50}
                step={1}
                marks={[{ value: 0, label: '0' }, { value: 25, label: '25' }, { value: 50, label: '50' }]}
              />
            </Box>
            {/* Poprzednik */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="pred-task-label">Poprzednik – zadanie</InputLabel>
                <Select labelId="pred-task-label" label="Poprzednik – zadanie" value={String(form.predecessor_task_id)} onChange={(e) => setForm(prev => ({ ...prev, predecessor_task_id: e.target.value }))}>
                  <MenuItem value="">Brak</MenuItem>
                  {items.map((it) => (
                    <MenuItem key={`pred-${it.id}`} value={it.id}>{`${it.id}. ${it.task}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="pred-type-label">Relacja</InputLabel>
                <Select labelId="pred-type-label" label="Relacja" value={form.predecessor_type} onChange={(e) => setForm(prev => ({ ...prev, predecessor_type: e.target.value as any }))}>
                  <MenuItem value="FS">FS (Zakończenie-Rozpoczęcie)</MenuItem>
                  <MenuItem value="FF">FF (Zakończenie-Zakończenie)</MenuItem>
                  <MenuItem value="SS">SS (Rozpoczęcie-Rozpoczęcie)</MenuItem>
                  <MenuItem value="SF">SF (Rozpoczęcie-Zakończenie)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            {/* Przesunięcie jako suwak */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Przesunięcie (dni): {form.predecessor_lag}</Typography>
              <Slider
                value={form.predecessor_lag}
                onChange={(_, v) => setForm(prev => ({ ...prev, predecessor_lag: Array.isArray(v) ? Number(v[0]) : Number(v) }))}
                min={-30}
                max={30}
                step={1}
                marks={[{ value: -30, label: '-30' }, { value: 0, label: '0' }, { value: 30, label: '+30' }]}
              />
            </Box>
            {/* Rozpoczęcie / Zakończenie – zakończenie zawsze wynikowe, backend liczy automatycznie */}
            {(() => {
              const toIso = (d: Date) => toLocalISO(d);
              const addDaysAbs = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
              const pid = Number(form.predecessor_task_id);
              let start = new Date(form.start_date + 'T00:00:00');
              if (pid) {
                const pred = items.find(it => it.id === pid);
                if (pred) {
                  const predStart = new Date(pred.startDate + 'T00:00:00');
                  const predEnd = new Date(pred.endDate + 'T00:00:00');
                  if (form.predecessor_type === 'FS') start = addDaysAbs(predEnd, 1 + (Number(form.predecessor_lag) || 0));
                  else if (form.predecessor_type === 'SS') start = addDaysAbs(predStart, Number(form.predecessor_lag) || 0);
                  else if (form.predecessor_type === 'FF') {
                    const endTmp = addDaysAbs(predEnd, Number(form.predecessor_lag) || 0);
                    start = addDaysAbs(endTmp, -Number(form.days) + 1);
                  } else { // SF
                    const endTmp = addDaysAbs(predStart, Number(form.predecessor_lag) || 0);
                    start = addDaysAbs(endTmp, -Number(form.days) + 1);
                  }
                }
              }
              const end = addDaysAbs(start, Math.max(0, Number(form.days) - 1));
              return (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField 
                    label={Boolean(pid) ? 'Rozpoczęcie (auto)' : 'Rozpoczęcie'}
                    name="start_date"
                    type="date"
                    value={Boolean(pid) ? toIso(start) : form.start_date}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputRef={startInputRef}
                    InputProps={{
                      readOnly: Boolean(pid),
                      endAdornment: (
                        <IconButton size="small" onClick={() => { const el: any = startInputRef.current; if (!el || Boolean(pid)) return; if (typeof el.showPicker === 'function') el.showPicker(); else el.focus(); }}>
                          <CalendarMonth fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                  <TextField 
                    label="Zakończenie (wynik)"
                    name="end_date"
                    type="date"
                    value={toIso(end)}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              );
            })()}
            {/* Wartość / Uwagi */}
            <TextField
              label="Wartość netto (PLN)"
              name="value"
              type="text"
              value={String(form.value ?? '')}
              onChange={(e) => {
                const raw = e.target.value.replace(/\s+/g, '');
                if (raw === '') { setForm(prev => ({ ...prev, value: '' as any })); return; }
                if (/^\d+$/.test(raw)) { setForm(prev => ({ ...prev, value: Math.max(0, Number(raw)) as any })); }
              }}
              onBlur={() => setForm(prev => ({ ...prev, value: Math.max(0, Number(prev.value) || 0) }))}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <TextField label="Uwagi" name="notes" value={form.notes} onChange={handleChange} fullWidth multiline rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Anuluj</Button>
          <Button variant="contained" onClick={handleSubmit}>Zapisz</Button>
        </DialogActions>
      </Dialog>

      {/* Powiadomienie */}
      <Notification open={notify.open} message={notify.message} type={notify.type} onClose={() => setNotify(prev => ({ ...prev, open: false }))} />

      {/* Menu i dialog uzupełniania stanu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={closeStatusMenu}>
        <MenuItem onClick={() => {
          closeStatusMenu();
          setStatusDialogOpen(true);
          const row = items.find(i => i.id === statusRowId);
          setStatusForm({ actual_progress: row?.actual_progress ?? 0, date: row?.actual_progress_date ?? new Date().toISOString().slice(0,10) });
        }}>Uzupełnij stan</MenuItem>
      </Menu>

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Kontrola zaawansowania</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption">Zaawansowanie: {statusForm.actual_progress}%</Typography>
            <Slider value={statusForm.actual_progress} onChange={(_, v) => setStatusForm({ ...statusForm, actual_progress: Array.isArray(v) ? Number(v[0]) : Number(v) })} min={0} max={100} step={1} />
            <TextField label="Data stanu" type="date" value={statusForm.date} onChange={(e) => setStatusForm({ ...statusForm, date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (statusRowId != null) {
                await projectService.updateScheduleItem(statusRowId, { actual_progress: statusForm.actual_progress, actual_progress_date: statusForm.date } as any);
                await load();
              }
            } catch (e) {
              // fallback lokalny
              if (statusRowId != null) {
                setItems(prev => prev.map(p => p.id === statusRowId ? { ...p, actual_progress: statusForm.actual_progress, actual_progress_date: statusForm.date } : p));
              }
            } finally {
              setStatusDialogOpen(false);
            }
          }}>Zapisz</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Widok (dawniej: Generuj PDF) */}
      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Widok</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Tryb</Typography>
            <ToggleButtonGroup size="small" exclusive value={pdfMode} onChange={(_, v) => v && setPdfMode(v)}>
              <ToggleButton value="baseline">Harmonogram bazowy</ToggleButton>
              <ToggleButton value="control">Kontrola stanu</ToggleButton>
            </ToggleButtonGroup>
            <Divider />
            <Typography variant="subtitle2">Skala czasu</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="pdf-scale-label">Skala czasu</InputLabel>
              <Select labelId="pdf-scale-label" label="Skala czasu" value={pdfScale} onChange={(e) => setPdfScale(e.target.value as any)}>
                <MenuItem value="year">Rok</MenuItem>
                <MenuItem value="month">Miesiące</MenuItem>
                <MenuItem value="day">Dni</MenuItem>
              </Select>
            </FormControl>
            <Divider />
            <Typography variant="subtitle2">Kolumny tabeli</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              <Checkbox checked={pdfCols.id} onChange={(e) => setPdfCols(prev => ({ ...prev, id: e.target.checked }))} /><Typography>ID</Typography>
              <Checkbox checked={pdfCols.ssp} onChange={(e) => setPdfCols(prev => ({ ...prev, ssp: e.target.checked }))} /><Typography>SSP</Typography>
              <Checkbox checked={pdfCols.name} onChange={(e) => setPdfCols(prev => ({ ...prev, name: e.target.checked }))} /><Typography>Zadanie</Typography>
              <Checkbox checked={pdfCols.days} onChange={(e) => setPdfCols(prev => ({ ...prev, days: e.target.checked }))} /><Typography>Czas [dni]</Typography>
              <Checkbox checked={!!pdfCols.start} onChange={(e) => setPdfCols(prev => ({ ...prev, start: e.target.checked }))} /><Typography>Rozpoczęcie</Typography>
              <Checkbox checked={!!pdfCols.end} onChange={(e) => setPdfCols(prev => ({ ...prev, end: e.target.checked }))} /><Typography>Zakończenie</Typography>
              <Checkbox checked={!!pdfCols.predecessor} onChange={(e) => setPdfCols(prev => ({ ...prev, predecessor: e.target.checked }))} /><Typography>Poprzednik</Typography>
              <Checkbox checked={!!pdfCols.value} onChange={(e) => setPdfCols(prev => ({ ...prev, value: e.target.checked }))} /><Typography>Wartość</Typography>
              <Checkbox checked={!!pdfCols.notes} onChange={(e) => setPdfCols(prev => ({ ...prev, notes: e.target.checked }))} /><Typography>Uwagi</Typography>
              <Checkbox checked={!!pdfCols.progress} onChange={(e) => setPdfCols(prev => ({ ...prev, progress: e.target.checked }))} /><Typography>Zaaw. [%]</Typography>
              <Checkbox checked={!!pdfCols.progressDate} onChange={(e) => setPdfCols(prev => ({ ...prev, progressDate: e.target.checked }))} /><Typography>Data stanu</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfDialogOpen(false)}>Anuluj</Button>
          <Button onClick={() => { setPdfDialogOpen(false); setZoom(pdfScale); setShowControl(pdfMode === 'control'); try { localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ cols: pdfCols, scale: pdfScale, mode: pdfMode })); } catch {} }}>Zapisz</Button>
          <Button variant="contained" onClick={() => {
            const prevZoom = zoom;
            setShowControl(pdfMode === 'control');
            setZoom(pdfScale);
            setPdfDialogOpen(false);
            setTimeout(async () => {
              const outer = ganttHiddenRef.current || ganttRef.current;
              if (!outer) return;
              const inner = outer.querySelector('[data-gantt-inner="1"]') as HTMLElement | null;
              const right = outer.querySelector('.right') as HTMLElement | null;
              const left = outer.querySelector('.left') as HTMLElement | null;
              // Przygotowanie: pokaż pełną szerokość wykresu razem z tabelą
              const prevRightOverflow = right?.style.overflow || '';
              const prevRightWidth = right?.style.width || '';
              const prevOuterWidth = outer.style.width || '';
              const innerW = inner ? inner.scrollWidth : (right ? right.scrollWidth : outer.scrollWidth);
              const leftW = left ? left.offsetWidth : 0;
              if (right) {
                right.style.overflow = 'visible';
                right.style.width = `${innerW}px`;
              }
              outer.style.width = `${leftW + innerW + 36 /* approx spacer */}px`;
              // Render całości (tabela + wykres)
              const scale = 2;
              const canvas = await html2canvas(outer, { scale, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0, windowWidth: outer.scrollWidth, windowHeight: outer.scrollHeight });
              const cssW = canvas.width / scale;
              const cssH = canvas.height / scale;
              const imgData = canvas.toDataURL('image/png');
              // Przygotuj nagłówek jako tabelę z tekstowym logo o stałej szerokości
              const headerEl = document.createElement('div');
              headerEl.style.position = 'absolute';
              headerEl.style.left = '-10000px';
              headerEl.style.top = '0';
              const headerTargetW = 560; // jeszcze węższa tabela
              headerEl.style.width = `${headerTargetW}px`;
              headerEl.style.background = '#ffffff';
              headerEl.style.fontFamily = 'Montserrat, Arial, Helvetica, "DejaVu Sans", sans-serif';
              headerEl.style.color = '#111';
              headerEl.style.padding = '0';
              const today = new Date().toISOString().slice(0,10);
              const title = pdfMode === 'control' ? `Kontrola stanu w dniu ${today}` : 'Harmonogram bazowy';
              headerEl.innerHTML = `
                <table style="width:${headerTargetW}px;border-collapse:collapse;font-size:11px;">
                  <tr>
                    <td style="width:160px;border-left:1px solid #000;border-top:1px solid #000;border-bottom:1px solid #000;border-right:1px solid #fff;padding:6px 8px 5px 8px;vertical-align:middle;">
                      <div style="font-family:Montserrat, Arial, Helvetica, 'DejaVu Sans', sans-serif;font-weight:800;font-size:20px;color:#c53030;letter-spacing:1px;">UNITRESCO</div>
                    </td>
                    <td style="border-top:1px solid #000;border-bottom:1px solid #000;border-right:1px solid #fff;border-left:1px solid #fff;padding:8px 10px;font-weight:700;font-size:12px;">${title}</td>
                  </tr>
                  <tr>
                    <td colspan="2" style="border-left:1px solid #000;border-right:1px solid #fff;border-bottom:1px solid #000;padding:0;">
                      <table style="width:100%;border-collapse:collapse;">
                        <tr>
                          <td style="width:160px;border-right:1px solid #fff;padding:6px 8px;font-weight:700;">Generalny wykonawca:</td>
                          <td style="padding:6px 8px;">${(printMeta.generalContractor||'').replace(/&/g,'&amp;')}</td>
                        </tr>
                        <tr>
                          <td style="width:160px;border-top:1px solid #000;border-right:1px solid #fff;padding:6px 8px;font-weight:700;">Zamawiający:</td>
                          <td style="border-top:1px solid #000;padding:6px 8px;">${(printMeta.client||'').replace(/&/g,'&amp;')}</td>
                        </tr>
                        <tr>
                          <td style="width:160px;border-top:1px solid #000;border-right:1px solid #fff;padding:6px 8px;font-weight:700;">Data harmonogramu:</td>
                          <td style="border-top:1px solid #000;padding:6px 8px;">${(printMeta.scheduleDate||'')}</td>
                        </tr>
                        <tr>
                          <td style="width:160px;border-top:1px solid #000;border-right:1px solid #fff;padding:6px 8px;font-weight:700;">Przedmiot umowy:</td>
                          <td style="border-top:1px solid #000;padding:6px 8px;">${((printMeta as any).contractSubject||'').replace(/&/g,'&amp;')}</td>
                        </tr>
                        <tr>
                          <td style="width:160px;border-top:1px solid #000;border-right:1px solid #fff;padding:6px 8px;font-weight:700;">Adres inwestycji:</td>
                          <td style="border-top:1px solid #000;padding:6px 8px;">${(printMeta.address||'').replace(/&/g,'&amp;')}</td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              `;
              document.body.appendChild(headerEl);
              const headerCanvas = await html2canvas(headerEl, { scale, backgroundColor: '#ffffff' });
              const headerImg = headerCanvas.toDataURL('image/png');
              const headerCssW = headerCanvas.width / scale;
              const headerCssH = headerCanvas.height / scale;
              document.body.removeChild(headerEl);

              // Składanie PDF – szerokość wg max(gantt, header)
              const pt = (px: number) => px * 0.75;
              const contentCssW = Math.max(cssW, headerCssW);
              const pageW = pt(contentCssW);
              const legalNoteMargin = 14; // px
              const pageH = pt(headerCssH + cssH + legalNoteMargin*2);
              const orientation = pageW >= pageH ? 'landscape' : 'portrait';
              const pdf = new jsPDF({ orientation: orientation as any, unit: 'pt', format: [pageW, pageH] });
              let yPt = 0;
              // Wstaw nagłówek w jego natywnej szerokości, wyrównany do lewej
              pdf.addImage(headerImg, 'PNG', 0, yPt, pt(headerCssW), pt(headerCssH));
              yPt += pt(headerCssH);
              // wykres rozciągnięty na całą szerokość strony
              pdf.addImage(imgData, 'PNG', 0, yPt, pageW, pt(cssH));
              yPt += pt(cssH);
              // Nota prawna jako tekst (searchable), wyrównana do lewej
              const footerText = '© 2025 Unitresco sp. z o. o. Wszystkie prawa zastrzeżone.';
              const noteFontPt = 4.8; // 20% mniej niż 6pt
              pdf.setFontSize(noteFontPt);
              const leftMarginPt = pt(12);
              const bottomMarginPt = pt(14);
              // Wyrównanie do lewej przy stałym marginesie, z poprawnym fontem dla PL znaków
              try {
                await ensurePolishFont(pdf);
                pdf.text(footerText, leftMarginPt, pageH - bottomMarginPt, { align: 'left', baseline: 'bottom' as any });
              } catch (e) {
                // Fallback bez polskich znaków, gdy font nie jest dostępny
                const ascii = '(c) 2025 Unitresco sp. z o. o. Wszystkie prawa zastrzezone.';
                pdf.text(ascii, leftMarginPt, pageH - bottomMarginPt, { align: 'left', baseline: 'bottom' as any });
              }
              // Zamiast bezpośredniego zapisu – wyślij do backendu na kompresję i zwróć skompresowany
              const blob = pdf.output('blob');
              const fd = new FormData();
              fd.append('file', blob, `harmonogram-${projectName}.pdf`);
              const resp = await fetch('/api/pdf/compress', { method: 'POST', body: fd });
              if (resp.ok) {
                const compressed = await resp.blob();
                const url = URL.createObjectURL(compressed);
                const a = document.createElement('a');
                a.href = url;
                a.download = `harmonogram-${projectName}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } else {
                // fallback do lokalnego zapisu jeśli kompresja nie działa
                pdf.save(`harmonogram-${projectName}.pdf`);
              }
              // Przywróć style
              if (right) {
                right.style.overflow = prevRightOverflow;
                right.style.width = prevRightWidth;
              }
              outer.style.width = prevOuterWidth;
              setZoom(prevZoom);
            }, 350);
          }}>Generuj PDF</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog ustawień wydruku (kosztorysu) */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ustawienia wydruku harmonogramu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Logo jest stałe – nie ma pola edycyjnego */}
            <TextField label="Generalny wykonawca" fullWidth value={printMeta.generalContractor} onChange={(e) => setPrintMeta(prev => ({ ...prev, generalContractor: e.target.value }))} />
            <TextField label="Zamawiający" fullWidth value={printMeta.client} onChange={(e) => setPrintMeta(prev => ({ ...prev, client: e.target.value }))} />
            <TextField label="Data harmonogramu" type="date" fullWidth InputLabelProps={{ shrink: true }} value={printMeta.scheduleDate} onChange={(e) => setPrintMeta(prev => ({ ...prev, scheduleDate: e.target.value }))} />
            <TextField label="Przedmiot umowy" fullWidth multiline minRows={3} value={(printMeta as any).contractSubject ?? ''} onChange={(e) => setPrintMeta(prev => ({ ...prev, contractSubject: e.target.value } as any))} />
            <TextField label="Adres inwestycji" fullWidth value={printMeta.address} onChange={(e) => setPrintMeta(prev => ({ ...prev, address: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Zamknij</Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>Zapisz</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ScheduleCard;


